
// Import modules
import { Component, HostListener, OnInit, Renderer2, Inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ThemeSwitcherService, FlowService, AuthService, NotifyService, VersionService, DateService } from 'mapfre-auditlib';
import * as _ from 'lodash';
import { Router } from '@angular/router';
import moment from 'moment';
import { MsalService, MsalBroadcastService, MSAL_GUARD_CONFIG, MsalGuardConfiguration } from '@azure/msal-angular';
import { AuthenticationResult, InteractionStatus, PopupRequest, RedirectRequest, EventMessage, EventType  } from '@azure/msal-browser';
import { Subject } from 'rxjs';
import { environment } from "../environments/environment";
import { PrimeNGConfig } from 'primeng/api';
import { filter, takeUntil } from 'rxjs/operators';

/** 
 * App root component
*/
@Component({
  selector: 'mapfre-auditmap-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements OnInit{

  // Generic modal variables
  visible: any;
  modal: any;
  key: any;

  // Variable for inactivity time
  inactivityLimit: any
  private readonly _destroying$ = new Subject<void>();
  timeLeft: any
  showInactivityModal: any

  // Render router
  renderApp: any;
  isIframe = false;

  /**
   * Constructor
   * @param versionService 
   * @param translate 
   * @param themeSwicther 
   * @param notifyService 
   * @param flowService 
   * @param router 
   * @param renderer 
   * @param msalService
   * @param authService 
   */
  constructor(
    private translate: TranslateService, 
    @Inject(MSAL_GUARD_CONFIG) private msalGuardConfig: MsalGuardConfiguration,
    private themeSwicther: ThemeSwitcherService,
    private notifyService: NotifyService,
    private flowService: FlowService, private router: Router,
    private renderer: Renderer2, private dateService: DateService,
    private authService: MsalService,
    private authCustomService: AuthService,
    private msalBroadcastService: MsalBroadcastService,
    private primeNGConfig: PrimeNGConfig) {

    // Check execution date
    this.checkExecution()

    // Event to change language (from browser)  
    this.translate.onLangChange.subscribe(res => {
      this.renderApp=true;
    })

    // Set main theme
    this.themeSwicther.changeTheme('CoreTheme');

    // Event to listen generic modal
    this.notifyService.observer.subscribe((msg: any) => {
      if (msg && (msg.key === 'changeModalVisibility' || msg.key === 'modal-comments' || msg.key === 'modal-comments-back-button'
        || msg.key === 'deletePaiModal')) {
        this.visible = msg.data;
        this.modal = msg.modal;
        this.key = msg.key
      }
    });

    // Restart inactivity time
    this.restartTimerInactivity()

    // Check evert second if time left is 0. If it's, logout app.
    setInterval(() => {

      // Current ime
      let currentTime = moment();

      // Check time
      let duration = moment.duration(this.inactivityLimit.diff(currentTime));
      let minutes: any = duration.minutes()
      let seconds: any = duration.seconds();

      if (minutes <= 0 && seconds <= 0 && this.authCustomService.isLogged()) {
        this.showInactivityModal = false;
        this.authService.logout();
      } 
      else if (minutes <= 0 && seconds <= 30 && this.authCustomService.isLogged()) {
        this.showInactivityModal = true;
      }
      minutes = minutes > 9 ? minutes.toString() : 0 + minutes.toString();
      seconds = seconds > 9 ? seconds.toString() : 0 + seconds.toString();

      this.timeLeft = `${minutes}:${seconds}`;
    }, 1000)
  }


  ngOnInit(): void {
    this.isIframe = window !== window.parent && !window.opener;
    this.msalBroadcastService.inProgress$
      .pipe(
        filter((status: InteractionStatus) => status === InteractionStatus.None),
        takeUntil(this._destroying$)
      )
      .subscribe(() => {
        this.checkAndSetActiveAccount();
      })
  }
 
  checkAndSetActiveAccount(){
    /**
     * If no active account set but there are accounts signed in, sets first account to active account
     * To use active account set here, subscribe to inProgress$ first in your component
     */
    let activeAccount = this.authService.instance.getActiveAccount();
    let SignedInAccounts = this.authService.instance.getAllAccounts();

    if (!activeAccount && SignedInAccounts.length > 0) {
      let accounts = this.authService.instance.getAllAccounts();
      this.authService.instance.setActiveAccount(accounts[0]);
      this.authCustomService.setConfigLogin()
    } else if (!activeAccount && SignedInAccounts.length === 0) {
        this.authService.loginRedirect({
          scopes: ['User.Read'],
          prompt: 'select_account'
        });
    }

  }

  /**
   * Events of generic modal
   * @param value 
   */
  sendResponse(value: any) {
    if (this.key === 'changeModalVisibility') {
      this.notifyService.notify({ key: 'changeModalConfirmation', data: value });
    }
    else if (this.key === 'modal-comments') {
      this.notifyService.notify({ key: 'response-modal-comments', data: value });
    }
    else if (this.key === 'modal-comments-back-button') {
      this.notifyService.notify({ key: 'response-modal-comments-back-button', data: value });
    }
    else if (this.key === 'deletePaiModal') {
      this.notifyService.notify({ key: 'deletePaiModalConfirmation', data: value });
    }

  }

  /**
   * Host listener to reset time left when press a key
   * @param event 
   */
  @HostListener('document:keydown', ['$event'])
  onKeydownHandler(event: KeyboardEvent) {
    this.restartTimerInactivity();
  }

  /**
   * Host listener to reset time left when mouse click
   * @param event 
   */
  @HostListener('window:click', ['$event'])
  onClick(event: any) {
    this.restartTimerInactivity();
  }

  /**
   * Host listener to reset time left when mouse move
   * @param event 
   */
  @HostListener('window:mousemove', ['$event'])
  onMouseMove(event: any) {
    this.restartTimerInactivity();
  }

  /** 
   * Restart time of inactivity
   */
  restartTimerInactivity() {
    this.inactivityLimit = moment().add(environment.MAX_INACTIVITY_MINUTES, 'minutes');
    this.showInactivityModal = false;
  }

  /** 
   * Check time of execution
   */
   checkExecution() {
    this.dateService.setAccessDate();
    if(this.authService.instance.getActiveAccount()){
      const userLanguage = localStorage.getItem(`language`);

      if(userLanguage) {
        this.translate.use(userLanguage);
      }
      
      this.translate.get('primeng').subscribe(res => this.primeNGConfig.setTranslation(res));
    }else {
      this.translate.use("es");
    }
  }

  loginRedirect() {
    if (this.msalGuardConfig.authRequest){
      this.authService.loginRedirect({...this.msalGuardConfig.authRequest} as RedirectRequest);
    } else {
      this.authService.loginRedirect();
    }
  }

  logout() {
    this.authService.logoutRedirect();
  }

  ngOnDestroy(): void {
    this._destroying$.next(undefined);
    this._destroying$.complete();
  }

}